/* TwoTierLayoutComponent.js */

'use strict';
const LayoutManager = require('@gluenetworks/layout_manager');
const Renderer = require('./TwoTierLayoutRenderer');
const _ = require('lodash');

class TwoTierLayoutComponent extends LayoutManager {
  constructor(logger, SteveDI, licenseRestriction) {
    super();
    this._logger = logger;
    this._di = SteveDI;
    this._licenseRestriction = licenseRestriction;

    this._pageParts = [];
    this._renderer = new Renderer(this, this._logger);
    this._options = {};

    this._selectedGroup = null;
    this._selectedPage = null;
    this._pageData = null;

    this._defaultSelectedGroup = null;
    this._defaultSelectedPage = null;

    //this._registerPageParts();
    this._registerListeners();
  }
  /*******************************************/
  /* START PRIVATE COMPONENT MODEL METHODS */
  /*******************************************/
  _registerListeners() {
    this.on('NamespaceGlobsChanged', this._onNamespaceChanged.bind(this));
    this._licenseRestriction.on(
      'Data',
      (this._onLicenseUpdated = this._onLicenseUpdated.bind(this))
    );
  }
  _unregisterListeners() {
    this._licenseRestriction.removeListener('Data', this._onLicenseUpdated);
  }

  _onNamespaceChanged() {
    this._logger.debug({args: arguments}, 'two tier layout parts globs changed');
    this._registerPageParts();
  }

  _onLicenseUpdated() {
    this._registerPageParts();
    this.emit('LicensesUpdated');
  }

  _registerPageParts() {
    this._pageParts = [];

    let allComponents = this.getAllItems();
    Object.keys(allComponents).forEach((ns) => {
      Object.keys(allComponents[ns]).forEach((moduleName) => {
        let regName = `${ns}.${moduleName}`;
        let regPart = allComponents[ns][moduleName];
        let groupName = regPart.registeredObj.$groupName || 'Other';
        let groupRouteName =
          regPart.registeredObj.$groupRouteName ||
          regPart.registeredObj.$groupName.replace(/ /g, '');

        let displayName = regPart.registeredObj.$displayName || 'Unknown';
        let pageRouteName =
          regPart.registeredObj.$pageRouteName ||
          regPart.registeredObj.$displayName.replace(/ /g, '');
        let pageLicense = regPart.registeredObj.$license;

        const licenseAvailable = this.checkLicenseIsAvailable(regPart.registeredObj.$license);
        if (licenseAvailable) {
          this._pageParts.push({
            moduleName: regName,
            groupName: groupName,
            groupRouteName: groupRouteName,
            displayName: displayName,
            pageRouteName: pageRouteName,
            pageLicense: pageLicense,
          });
        }
      });
    });

    //Set Default and Initial Selection.
    this._defaultSelectedGroup = this._pageParts[0].groupRouteName;
    this._defaultSelectedPage = this._pageParts[0].pageRouteName;
    this._selectedGroup = this._selectedGroup || this._pageParts[0].groupRouteName;
    this._selectedPage = this._selectedPage || this._pageParts[0].pageRouteName;
  }
  _groupExists(groupName) {
    let result = false;
    if (groupName) {
      this._pageParts.forEach((obj) => {
        if (obj.groupRouteName === groupName) {
          result = true;
        }
      });
    }
    return result;
  }
  _groupAndPageExists(groupName, pageName) {
    let result = false;
    if (groupName && pageName) {
      this._pageParts.forEach((obj) => {
        if (obj.groupRouteName === groupName && obj.pageRouteName === pageName) {
          result = true;
        }
      });
    }
    return result;
  }
  _currentPageExists() {
    return this._groupAndPageExists(this._selectedGroup, this._selectedPage);
  }
  _pageLicense(pageName) {
    const pagePart = this._pageParts.find((pagePart) => pagePart.pageRouteName === pageName);
    return pagePart && pagePart.pageLicense;
  }
  _checkAndSetLayout(groupName, pageName, pageData) {
    //Start with defaults
    this._selectedGroup = this._defaultSelectedGroup;
    this._selectedPage = this._defaultSelectedPage;
    this._pageData = null;

    if (this._groupAndPageExists(groupName, pageName)) {
      this._selectedGroup = groupName;
      this._selectedPage = pageName;
      if (pageData) {
        this._pageData = pageData;
      }
    } else if (this._groupExists(groupName)) {
      let part = this._pageParts.find((obj) => {
        return obj.groupRouteName === groupName;
      });

      if (part) {
        this._selectedGroup = part.groupRouteName;
        this._selectedPage = part.pageRouteName;
      }
    }
    return this.renderer.render();
  }
  _sendRouteEvent(groupName, pageName) {
    let subRoute = groupName;
    if (!pageName) {
      let part = this._pageParts.find((obj) => {
        return obj.groupRouteName === groupName;
      });
      pageName = part.pageRouteName;
    }
    subRoute += '/' + pageName;
    this.emit('UpdateRoute', {sr: subRoute});
  }
  /*******************************************/
  /* END PRIVATE COMPONENT MODEL METHODS */
  /* START PUBLIC COMPONENT MODEL METHODS */
  /*******************************************/
  get renderer() {
    return this.getRenderer();
  }
  getRenderer() {
    return this._renderer;
  }
  shutdown() {
    let prom = new Promise((resolve /*,reject*/) => {
      this.emit('Shutdown');
      this._unregisterListeners();
      this._logger = undefined;
      this._renderer = undefined;
      resolve();
    });
    return prom;
  }
  get DI() {
    return this.getDI();
  }
  getDI() {
    return this._di;
  }
  get pageParts() {
    return this.getPageParts();
  }
  getPageParts() {
    return this._pageParts;
  }
  organizationChanged(newOrg) {
    const currentPageLicense = this._pageLicense(this._selectedPage);
    this._licenseRestriction.whenLicenseFeedIsReady().then(() => {
      if (this._currentPageExists() && this.checkLicenseIsAvailable(currentPageLicense)) {
        this.emit('OrganizationChanged', newOrg);
      } else {
        this.setSelectedGroup(this._defaultSelectedGroup);
      }
    });
  }
  get selectedGroup() {
    return this.getSelectedGroup();
  }
  set selectedGroup(name) {
    this.setSelectedGroup(name);
  }
  getSelectedGroup() {
    return this._selectedGroup;
  }
  setSelectedGroup(groupName) {
    this._sendRouteEvent(groupName);
  }
  get selectedPage() {
    return this.getSelectedPage();
  }
  set selectedPage(name) {
    this.setSelectedPage(name);
  }
  getSelectedPage() {
    return this._selectedPage;
  }
  setSelectedPage(pageName) {
    this._sendRouteEvent(this._selectedGroup, pageName);
  }
  get pageData() {
    return this.getPageData();
  }
  set pageData(data) {
    this.setPageData(data);
  }
  getPageData() {
    return this._pageData;
  }
  setPageData(data) {
    this._pageData = data;
  }
  setLayoutSelections(options) {
    let mergedOpts = _.defaults({}, options);
    return this._checkAndSetLayout(mergedOpts.groupName, mergedOpts.pageName, mergedOpts.pageData);
  }
  /*******************************************/
  /* END COMPONENT MODEL METHODS */
  /* START HANDLER METHODS */
  /*******************************************/

  /*******************************************/
  /* END HANDLER METHODS */
  /* START PRIVATE METHODS */
  /*******************************************/

  /*******************************************/
  /* END PRIVATE METHODS */
  /* START PUBLIC API METHODS */
  /*******************************************/
  checkLicenseIsAvailable(licenseCodes) {
    return this._licenseRestriction.checkLicense(licenseCodes);
  }
  /*******************************************/
  /* END PUBLIC API METHODS */
  /*******************************************/
}
module.exports = TwoTierLayoutComponent;
module.exports.$inject = ['$teve.logger', '$teve.di', '$teve.licenseRestriction'];
